import { Link, Accordion, ListItemText, AccordionDetails, AccordionSummary, Box, Typography, List, ListItem, Table, TableContainer, Paper, TableHead, TableBody, TableRow, TableCell, Stack, useTheme, useMediaQuery, Card, CardContent } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface Props {
    isPac?: boolean;
    isLeg?: boolean;
    isStateParty?: boolean;
    lateContributionSummary?: string;
}

const OtherReportsTable = (props: Props): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const requiredOtherReports = [
        {
            report: 'Bank Report',
            description: 'This report provides beginning and ending balance information, a summary of the deposit activity in the account, and a detailed listing of all expenditure activity for each depository campaign account for the reporting period.',
            filedBy: 'Bank',
            deadline: '5th of each month'
        },
        {
            report: 'Deposit Report',
            description: 'This report provides itemized contribution information for each deposit made into the campaign account.',
            filedBy: 'Filer',
            deadline: props.isLeg ? 'Refer to table above' : '5th of each month'
        },
        {
            report: 'Year-End Report',
            description: 'Depository candidates and committees also file a summary year-end report. The year-end report summarizes all deposit and expenditure activity for the year, lists any savings accounts (and balances), and provides disclosure of all in-kind contributions received or outstanding liabilities. Most of this report is pre-populated by OCPF using bank information.',
            filedBy: 'Filer',
            deadline: 'January 20th of the following year'
        },

    ]

    const otherReports = [

        {
            report: 'Credit Card Report',
            description:
                <Typography variant='body2'>
                    This report provides information about the underlying expenditure activity for each statement period of the committee's credit card account.
                    Please refer to <Link href='/Legal/Regulations' target='_blank'>970 CMR 2.09(3)(h)</Link> for more information.
                    This report is only filed if applicable.
                </Typography>,
            filedBy: 'Filer',
            deadline: props.isLeg ? 'Refer to table above' : '5th of each month'
        },
        {
            report: 'Reimbursement Report',
            description:
                <Typography variant='body2'>
                    This report provides information about the underlying expenditure activity for each third-party reimbursement made.
                    Please refer to <Link target='_blank' href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/M-97-04.pdf">M-97-04</Link> for more information.
                    This report is only filed if applicable.
                </Typography>,
            filedBy: 'Filer',
            deadline:  'Within 30 days of making expenditure' 
        },
        {
            report: 'Subvendor Report',
            description:
                <Typography variant='body2'>
                    This report provides information about the underlying expenditure activity of third-party vendors subcontracted by vendors to provide goods and services to the candidate or committee.
                    Please refer to <Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-04.pdf' target='_blank'>IB-10-04</Link> for more information.
                    This report is only filed if applicable.
                </Typography>,
            filedBy: 'Filer',
            deadline: props.isLeg ? 'Refer to table above' : '5th of each month'
        },
        {
            report: 'Payroll Report',
            description:
                <Typography variant='body2'>
                    Payroll Itemization reports are filed when a committee pays a third-party vendor, such as ADP, for the committee's payroll.
                    The payroll report itemizes the net amount for each payroll recipient.
                    Please refer to <Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/M-14-01.pdf' target='_blank'>M-14-01</Link> for more information.
                    This report is only filed if applicable.

                </Typography>,
            filedBy: 'Filer',
            deadline: props.isLeg ? 'Refer to table above' : 'Within 7 business days of Bank Report filing date'
        },
        {
            report: props.isStateParty ? '24-Hour (Late) Contribution Report' : '72-Hour (Late) Contribution Report',
            description:
                props.isStateParty ?
                    <Typography variant='body2'>
                        Depository candidates and committees that receive and deposit a contribution of $500 or more after the 18th day, but more than 24 hours,
                        before the date of a special, preliminary, primary or general election must disclose the contribution within 24 hours of deposit.
                        Depository filers submit a Deposit Report to disclose late contributions.
                        Please refer to <Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-02.pdf' target='_blank'>IB-10-02</Link> for more information.
                        This report is only filed if applicable.
                        {props.lateContributionSummary && <><br /><br /><b>For the most recent election year: </b>{props.lateContributionSummary}</>}
                        <br /><br />Late contribution reports are also filed for in-kind contributions, but not by filing a Deposit Report.
                        Click <Link href='https://www.youtube.com/watch?v=fj-fqKYeVI0' target='_blank'>here</Link> for instructions.
                    </Typography>
                    :
                    <Typography variant='body2'>
                        Depository candidates and committees that receive and deposit a contribution of $500 or more after the 18th day, but more than 72 hours,
                        before the date of a special, preliminary, primary or general election must disclose the contribution within 72 hours of deposit.
                        Depository filers submit a Deposit Report to disclose late contributions.
                        Please refer to <Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-02.pdf' target='_blank'>IB-10-02</Link> for more information.
                        This report is only filed if applicable.
                        {props.lateContributionSummary && <><br /><br /><b>For the most recent election year: </b>{props.lateContributionSummary}</>}
                        <br /><br />Late contribution reports are also filed for in-kind contributions, but not by filing a Deposit Report.
                        Click <Link href='https://www.youtube.com/watch?v=fj-fqKYeVI0' target='_blank'>here</Link> for instructions.
                    </Typography>,
            filedBy: 'Filer',
            deadline: props.isStateParty ? 'Within 24 hours of deposit' : 'Within 72 hours of deposit'
        },
      
        {
            report: 'Bank Expenditure Itemization Report',
            description:
                <Typography variant='body2'>
                    Traditional PACs, which may only contribute $500 per calendar year to candidates, sometimes make independent expenditures to support or oppose candidates. Independent expenditures are unlimited.
                    If a PAC makes an independent expenditure to support or oppose candidates, the PAC’s bank will disclose the expenditure on a monthly report. The PAC is then required to itemize the expenditure to disclose which candidates were supported and/or opposed.
                    Please refer to <Link href='http://localhost:3000/Legal/CampaignFinanceLaw' target='_blank'>M.G.L. Chapter 55 Section 19(g)</Link> for more information.
                </Typography>,
            filedBy: 'Filer',
            deadline: 'Within 7 business days of Bank Report filing date'
        }

    ];


    return (
        <Box>

            {
                !isSM &&
                <TableContainer>
                    <Table>

                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Report
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Description
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Filed By
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Deadline
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableHead>
                            <TableRow>
                                <TableCell align='center' colSpan={4}>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Principal Reports
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requiredOtherReports.map((report, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant='body2' color="primary">
                                            {report.report}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{report.description}</TableCell>
                                    <TableCell>{report.filedBy}</TableCell>
                                    <TableCell>{report.deadline}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>


                        <TableHead>
                            <TableRow>
                                <TableCell align='center' colSpan={4}>
                                    <Typography variant='subtitle2' fontWeight={550}>
                                        Supplemental Reports
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {otherReports.slice(0, otherReports.length - 1).map((report, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant='body2' color="primary">
                                            {report.report}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{report.description}</TableCell>
                                    <TableCell>{report.filedBy}</TableCell>
                                    <TableCell>{report.deadline}</TableCell>
                                </TableRow>
                            ))}
                            {(props.isPac || props.isStateParty) && otherReports.slice(otherReports.length - 1).map((report, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant='body2' color="primary">
                                            {report.report}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{report.description}</TableCell>
                                    <TableCell>{report.filedBy}</TableCell>
                                    <TableCell>{report.deadline}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
            {
                /*mobile view*/
                isSM &&
                otherReports.slice(0, otherReports.length - 1).map((report, i) =>
                    <Card variant="outlined" key={i} sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Stack spacing={1}>
                                <Typography variant="subtitle1" color="primary">
                                    {report.report}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Filed By: </b>{report.filedBy}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Deadline: </b>{report.deadline}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Description: </b>{report.description}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                )


            }

            {isSM && otherReports.slice(otherReports.length - 1).map((report, i) =>
                <Card variant="outlined" key={i} sx={{ marginBottom: 2 }}>
                    <CardContent>
                        <Stack spacing={1}>
                            <Typography variant="subtitle1" color="primary">
                                {report.report}
                            </Typography>
                            <Typography variant="body2">
                                <b>Filed By: </b>{report.filedBy}
                            </Typography>
                            <Typography variant="body2">
                                <b>Deadline: </b>{report.deadline}
                            </Typography>
                            <Typography variant="body2">
                                <b>Description: </b>{report.description}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            )}


        </Box>
    );
};

export default OtherReportsTable;